/**
 * MultiParcelDrawer merge/split orchestration (local + store persist).
 */
import type { SiteEntity } from '../types/site';
import type { SiteBoundaryParcel } from '../types/siteBoundary';
import { logAuditEvent } from './telemetry/auditTelemetry';
import {
  computeAggregate,
  polygonArea,
  polygonCentroid,
  polygonPerimeter,
  splitPolygonByLine,
  unionParcelPolygons,
} from './siteBoundaryGeometry';
import { mergeParcelsOnSite, splitParcelOnSite } from './siteParcelStoreActions';

export type ParcelHistoryKind = 'merge' | 'split' | 'edit';

export interface ParcelHistoryEntry {
  kind: ParcelHistoryKind;
  before: SiteBoundaryParcel[];
  after: SiteBoundaryParcel[];
}

export interface ParcelTopologyTelemetry {
  operationId: string;
  beforeParcels: SiteBoundaryParcel[];
  afterParcels: SiteBoundaryParcel[];
  beforeCount: number;
  afterCount: number;
  acreageBefore: number;
  acreageAfter: number;
  acreageDelta: number;
}

function sumAreas(parcels: SiteBoundaryParcel[]): number {
  return parcels.reduce((s, p) => s + (Number.isFinite(p.area) ? p.area : 0), 0);
}

export function buildTopologyTelemetry(
  before: SiteBoundaryParcel[],
  after: SiteBoundaryParcel[],
): ParcelTopologyTelemetry {
  const acreageBefore = sumAreas(before);
  const acreageAfter = sumAreas(after);
  return {
    operationId: crypto.randomUUID(),
    beforeParcels: before,
    afterParcels: after,
    beforeCount: before.length,
    afterCount: after.length,
    acreageBefore,
    acreageAfter,
    acreageDelta: acreageAfter - acreageBefore,
  };
}

export function logParcelTopologyChange(
  siteId: string,
  type: 'parcel-merged' | 'parcel-split',
  telemetry: ParcelTopologyTelemetry,
): void {
  logAuditEvent({
    type,
    siteId,
    payload: {
      ...telemetry,
      enriched: true,
    },
  });
}

function recomputeParcel(parcel: SiteBoundaryParcel, polygon: Array<{ lat: number; lng: number }>): SiteBoundaryParcel {
  return {
    ...parcel,
    polygon,
    area: parseFloat(polygonArea(polygon).toFixed(1)),
    perimeter: parseFloat(polygonPerimeter(polygon).toFixed(2)),
    centroid: polygonCentroid(polygon),
  };
}

export function mergeParcelsLocal(
  parcels: SiteBoundaryParcel[],
  selectedIds: string[],
): SiteBoundaryParcel[] | null {
  if (selectedIds.length < 2) return null;
  const selected = parcels.filter(p => selectedIds.includes(p.id));
  const unionPoly = unionParcelPolygons(selected);
  if (!unionPoly) return null;

  const targetId = selectedIds[0]!;
  const patched = parcels.map(p =>
    p.id === targetId ? recomputeParcel(p, unionPoly) : p,
  );
  const mockSite = { siteBoundaries: patched } as SiteEntity;
  const updates = mergeParcelsOnSite(mockSite, selectedIds, targetId);
  return updates?.siteBoundaries ?? null;
}

export function splitParcelLocal(
  parcels: SiteBoundaryParcel[],
  parcelId: string,
  lineA: { lat: number; lng: number },
  lineB: { lat: number; lng: number },
): SiteBoundaryParcel[] | null {
  const source = parcels.find(p => p.id === parcelId);
  if (!source) return null;
  const split = splitPolygonByLine(source.polygon, lineA, lineB);
  if (!split) return null;

  const mockSite = { siteBoundaries: parcels } as SiteEntity;
  const updates = splitParcelOnSite(mockSite, parcelId, split);
  return updates?.siteBoundaries ?? null;
}

export interface ParcelStoreActions {
  updateParcelPolygon: (
    siteId: string,
    parcelId: string,
    polygon: Array<{ lat: number; lng: number }>,
  ) => Promise<void>;
  mergeParcels: (siteId: string, sourceIds: string[], targetId: string) => Promise<void>;
  splitParcel: (
    siteId: string,
    parcelId: string,
    polygons: Array<Array<{ lat: number; lng: number }>>,
  ) => Promise<void>;
}

export async function persistMergeParcels(
  siteId: string,
  parcels: SiteBoundaryParcel[],
  selectedIds: string[],
  store: ParcelStoreActions,
): Promise<SiteBoundaryParcel[] | null> {
  const next = mergeParcelsLocal(parcels, selectedIds);
  if (!next) return null;

  const selected = parcels.filter(p => selectedIds.includes(p.id));
  const unionPoly = unionParcelPolygons(selected);
  if (!unionPoly) return null;

  const targetId = selectedIds[0]!;
  await store.updateParcelPolygon(siteId, targetId, unionPoly);
  await store.mergeParcels(siteId, selectedIds, targetId);
  return next;
}

export async function persistSplitParcel(
  siteId: string,
  parcels: SiteBoundaryParcel[],
  parcelId: string,
  lineA: { lat: number; lng: number },
  lineB: { lat: number; lng: number },
  store: ParcelStoreActions,
): Promise<SiteBoundaryParcel[] | null> {
  const source = parcels.find(p => p.id === parcelId);
  if (!source) return null;
  const split = splitPolygonByLine(source.polygon, lineA, lineB);
  if (!split) return null;

  await store.splitParcel(siteId, parcelId, split);
  return splitParcelLocal(parcels, parcelId, lineA, lineB);
}

export function aggregateAcreage(parcels: SiteBoundaryParcel[]): number {
  return computeAggregate(parcels).totalArea;
}
